import {
  PlayerAccessRequiredError,
  PlayerSessionUnauthorizedError,
  type PlayerChatCapabilities,
} from '../api/public.ts'
import { playerDockErrorMessage } from './playerSocialPresentation.ts'

export type PlayerSocialDockOptions = {
  authenticated: () => boolean
  steamID: () => string
  chatCapabilities: () => PlayerChatCapabilities
  onUnauthorized: () => void
  onAccessRequired?: () => void
  onPartyChanged: () => void
  onCommunicationPolicyChanged?: (observedAt: number) => void
  onIncomingChatMessage?: (
    eventKey: string,
    observedAt: number,
  ) => void
  onIncomingSocialNotification?: (
    eventKey: string,
    observedAt: number,
  ) => void
}

export type PlayerSocialDockSession = {
  options: PlayerSocialDockOptions
  generation: () => number
  isCurrentActor: (generation: number, actorSteamID: string) => boolean
  isCurrentActorRequest: (
    generation: number,
    actorSteamID: string,
    requestID: number,
    currentRequestID: number,
  ) => boolean
  handleRequestError: (
    error: unknown,
    target: { value: string | null },
    messageForError?: (error: unknown) => string,
  ) => void
}

export function createPlayerSocialDockSession(
  options: PlayerSocialDockOptions,
  generation: () => number,
): PlayerSocialDockSession {
  function isCurrentActor(
    expectedGeneration: number,
    actorSteamID: string,
  ): boolean {
    return (
      expectedGeneration === generation() &&
      Boolean(actorSteamID) &&
      actorSteamID === options.steamID() &&
      options.authenticated()
    )
  }

  function isCurrentActorRequest(
    expectedGeneration: number,
    actorSteamID: string,
    requestID: number,
    currentRequestID: number,
  ): boolean {
    return (
      requestID === currentRequestID &&
      isCurrentActor(expectedGeneration, actorSteamID)
    )
  }

  function handleRequestError(
    error: unknown,
    target: { value: string | null },
    messageForError: (error: unknown) => string = playerDockErrorMessage,
  ): void {
    if (error instanceof PlayerSessionUnauthorizedError) {
      target.value = null
      options.onUnauthorized()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      target.value = null
      options.onAccessRequired?.()
      return
    }
    target.value = messageForError(error)
  }

  return {
    options,
    generation,
    isCurrentActor,
    isCurrentActorRequest,
    handleRequestError,
  }
}
